import React, { useRef, useState } from "react";
import { Microphone, Stop, CircleNotch, Sparkle, X, Check } from "@phosphor-icons/react";
import { transcribeSymptomAudio, parseVoiceEntry } from "@/lib/api";

/**
 * "Speak your entry" — records a voice note, transcribes it (Whisper) and asks
 * the LLM to structure it into form fields (severity, meds, impact, ...). The
 * user reviews the parsed suggestion and taps APPLY to fill the form. Audio is
 * never persisted. Renders nothing when MediaRecorder is unsupported.
 */
export default function VoiceSmartFill({ onApply }) {
  const supported = typeof window !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia
    && typeof window.MediaRecorder !== "undefined";

  const recRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | recording | busy
  const [preview, setPreview] = useState(null); // { fields, transcript }
  const [err, setErr] = useState("");

  if (!supported) return null;

  async function start() {
    setErr(""); setPreview(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = window.MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mime || "audio/webm" });
        if (blob.size < 1200) { setState("idle"); return; }
        setState("busy");
        try {
          const { text } = await transcribeSymptomAudio(blob);
          if (!text) { setErr("No speech detected — try again closer to the mic."); return; }
          const { fields } = await parseVoiceEntry(text);
          setPreview({ fields, transcript: text });
        } catch (e2) {
          setErr(e2.response?.data?.detail || e2.message);
        } finally {
          setState("idle");
        }
      };
      rec.start();
      recRef.current = rec;
      setState("recording");
    } catch {
      setErr("Microphone unavailable or permission denied.");
    }
  }

  function stop() {
    try { recRef.current?.stop(); } catch { setState("idle"); }
  }

  function apply() {
    if (preview?.fields) onApply(preview.fields, preview.transcript);
    setPreview(null);
  }

  const rows = preview ? previewRows(preview.fields) : [];

  return (
    <div className="voice-smartfill" data-testid="voice-smartfill">
      {!preview && (
        <div className="voice-smartfill-bar">
          <Sparkle size={14} weight="fill" style={{ color: "#1F3A6B", flexShrink: 0 }} />
          <span className="voice-smartfill-label">
            Don't want to type? Speak your entry — "left foot flared, pain 8 out of 10, took indomethacin, could barely walk."
          </span>
          {state === "idle" && (
            <button type="button" className="voice-note-btn" onClick={start} data-testid="voice-smartfill-btn">
              <Microphone size={12} weight="bold" /> SPEAK ENTRY
            </button>
          )}
          {state === "recording" && (
            <button type="button" className="voice-note-btn voice-note-btn--rec" onClick={stop} data-testid="voice-smartfill-stop">
              <Stop size={12} weight="fill" /> STOP
            </button>
          )}
          {state === "busy" && (
            <span className="voice-note-btn" style={{ cursor: "default" }} data-testid="voice-smartfill-busy">
              <CircleNotch size={12} weight="bold" className="spin" /> Structuring...
            </span>
          )}
        </div>
      )}
      {err && <div style={{ fontSize: 11, color: "#C72E2E", marginTop: 4 }} data-testid="voice-smartfill-error">{err}</div>}
      {preview && (
        <div className="voice-smartfill-preview" data-testid="voice-smartfill-preview">
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", color: "#1F3A6B", marginBottom: 6 }}>
            HEARD &amp; STRUCTURED — REVIEW BEFORE APPLYING
          </div>
          <div style={{ fontSize: 11.5, color: "#475569", fontStyle: "italic", marginBottom: 8 }}>
            "{preview.transcript}"
          </div>
          {rows.length > 0 ? rows.map(([label, value]) => (
            <div key={label} style={{ display: "flex", gap: 8, fontSize: 12, marginBottom: 3 }}>
              <span style={{ fontWeight: 800, color: "#1F3A6B", minWidth: 90 }}>{label}</span>
              <span>{value}</span>
            </div>
          )) : (
            <div style={{ fontSize: 12, color: "#64748B" }}>Couldn't pick out structured fields — the transcript will go into Notes.</div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" className="btn btn--accent" style={{ padding: "6px 12px", fontSize: 11 }} onClick={apply} data-testid="voice-smartfill-apply">
              <Check size={12} weight="bold" /> APPLY TO FORM
            </button>
            <button type="button" className="btn btn--outline" style={{ padding: "6px 12px", fontSize: 11 }} onClick={() => setPreview(null)} data-testid="voice-smartfill-dismiss">
              <X size={12} weight="bold" /> DISMISS
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function previewRows(f = {}) {
  const rows = [];
  if (f.symptom) rows.push(["Symptom", f.symptom]);
  if (f.conditionHint) rows.push(["Condition", f.conditionHint]);
  if (f.bodyPart || f.bodySide) rows.push(["Body part", [f.bodySide, f.bodyPart].filter(Boolean).join(" ")]);
  if (f.severity != null) rows.push(["Severity", `${f.severity}/10`]);
  if (f.duration) rows.push(["Duration", f.duration]);
  if ((f.medications || []).length) rows.push(["Medication", f.medications.map((m) => m.name + (m.helped ? ` (${m.helped.toLowerCase()})` : "")).join(", ")]);
  if ((f.triggers || []).length) rows.push(["Triggers", f.triggers.join(", ")]);
  if ((f.impact || []).length) rows.push(["Impact", f.impact.join(", ")]);
  if ((f.missedActivities || []).length) rows.push(["Missed", f.missedActivities.join(", ")]);
  if (f.worstDay) rows.push(["Worst day", "Yes"]);
  return rows;
}
